import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import http from "../../../http-common";
import {
  listCollaborators,
  addCollaborator,
  removeCollaborator,
} from "../../../api/epks";

export default function CollaboratorModal({ epkId, onClose }) {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);
  const token = user?.token;

  const [crew, setCrew] = useState([]);
  const [collaboratorIds, setCollaboratorIds] = useState(new Set());
  const [togglingId, setTogglingId] = useState(null);
  const [error, setError] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [epkRes, collabs] = await Promise.all([
          http.get(`/fepks/${epkId}`),
          listCollaborators(epkId, token),
        ]);
        const actors = epkRes.data.actors || [];
        const crewMembers = (epkRes.data.crew || [])
          .filter((c) => c.crewId)
          .map((c) => ({ ...c.crewId, epkRole: c.epkRole }));
        const combined = [...actors, ...crewMembers].filter(
          (member) => member._id !== user?.id
        );
        const uniqueMembers = Array.from(
          new Map(combined.map((member) => [member._id, member])).values()
        );
        setCrew(uniqueMembers);
        setCollaboratorIds(new Set(collabs.map((c) => c.user._id)));
      } catch {
        setLoadFailed(true);
        setError(t("Failed to load crew"));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [epkId, token, t, user?.id]);

  const handleToggle = async (member) => {
    const id = member._id;
    const isCollab = collaboratorIds.has(id);
    setTogglingId(id);
    setError("");
    try {
      if (isCollab) {
        await removeCollaborator(epkId, id, token);
        setCollaboratorIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      } else {
        await addCollaborator(epkId, id);
        setCollaboratorIds((prev) => new Set([...prev, id]));
      }
    } catch (err) {
      setError(err.response?.data?.message || t("Failed to update collaborator"));
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/60">
      <div className="tw-w-full tw-max-w-md tw-rounded-xl tw-bg-[#1C0039] tw-border tw-border-[#712CB0]/30 tw-p-6 tw-shadow-xl">

        <div className="tw-mb-5 tw-flex tw-items-center tw-justify-between">
          <h2 className="tw-m-0 tw-text-lg tw-font-semibold tw-text-white">
            {t("Manage Collaborators")}
          </h2>
          <button
            onClick={onClose}
            className="tw-text-gray-400 hover:tw-text-white tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-lg tw-leading-none"
          >
            ✕
          </button>
        </div>

        {error && (
          <p className="tw-mb-3 tw-text-sm tw-text-red-400">{error}</p>
        )}

        {loading ? (
          <p className="tw-text-sm tw-text-white/50">{t("Loading...")}</p>
        ) : loadFailed ? null : crew.length === 0 ? (
          <p className="tw-text-sm tw-text-white/50 tw-text-center tw-py-8">
            {t("Add cast and crew to your EPK first to manage edit access.")}
          </p>
        ) : (
          <ul className="tw-m-0 tw-list-none tw-p-0 tw-divide-y tw-divide-white/10">
            {crew.map((member) => {
              const imageUrl = member.picture
                ? member.picture.startsWith("http")
                  ? member.picture
                  : `${process.env.REACT_APP_AWS_URL}/${member.picture}`
                : null;
              const isCollab = collaboratorIds.has(member._id);
              const isToggling = togglingId === member._id;

              return (
                <li
                  key={member._id}
                  className="tw-flex tw-items-center tw-justify-between tw-py-3"
                >
                  <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt=""
                        className="tw-h-10 tw-w-10 tw-rounded-full tw-object-cover tw-flex-shrink-0"
                      />
                    ) : (
                      <div className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-[#712CB0]/20 tw-flex-shrink-0">
                        <FontAwesomeIcon icon={faUser} className="tw-text-[#712CB0]" />
                      </div>
                    )}
                    <div className="tw-min-w-0">
                      <p className="tw-m-0 tw-text-sm tw-font-medium tw-text-white tw-truncate">
                        {member.firstName} {member.lastName}
                      </p>
                      <p className="tw-m-0 tw-text-xs tw-text-white/40">{member.epkRole || member.role}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => handleToggle(member)}
                    disabled={isToggling}
                    title={isCollab ? t("Remove edit access") : t("Grant edit access")}
                    className={`tw-relative tw-ml-4 tw-h-6 tw-w-11 tw-flex-shrink-0 tw-rounded-full tw-border-none tw-cursor-pointer tw-transition-colors tw-duration-200 disabled:tw-opacity-50 ${
                      isCollab ? "tw-bg-[#712CB0]" : "tw-bg-white/20"
                    }`}
                  >
                    <span
                      className={`tw-absolute tw-top-0.5 tw-left-0.5 tw-h-5 tw-w-5 tw-rounded-full tw-bg-white tw-shadow tw-transition-transform tw-duration-200 ${
                        isCollab ? "tw-translate-x-5" : "tw-translate-x-0"
                      }`}
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
